import { IconArrowLeft, IconLoader2 } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState, useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { NSSelect } from 'bricks';
import { MemoizedDropdownIndicator } from 'bricks/NSSelect/NSSelect';
import { IOptionAnyValue } from 'bricks/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { DEVELOPMENT_MANAGER_ASSOCIATION_TYPE } from 'views/Deals/constants';
import {
    components,
    PlaceholderProps,
    SingleValueProps,
    ClearIndicatorProps,
} from 'react-select';
import { useAttributeSuggested } from 'components/DealForm/hooks/useSuggestedAttribute';
import { DefaultAttributes, IFormValues } from '../../types';
import DevelopmentManagerTypeOption from './DevelopmentManagerTypeOption';
import {
    BACK_OPTION_VALUE,
    DEVELOPMENT_MANAGER_FIELD_VALUE,
    PLACEHOLDER_TEXT,
    DEVELOPMENT_MANAGER_TYPE_TITLES,
} from './constants';

interface DevelopmentManagerInputProps {
  showSuggestedOnly: boolean;
  userOptions: IOptionAnyValue[];
  usersAreLoading: boolean;
  contactOptions: IOptionAnyValue[];
  contactsAreLoading: boolean;
  companyOptions: IOptionAnyValue[];
  companiesAreLoading: boolean;
}

const DevelopmentManagerInput: React.FC<DevelopmentManagerInputProps> = ({
    showSuggestedOnly,
    userOptions,
    usersAreLoading,
    contactOptions,
    contactsAreLoading,
    companyOptions,
    companiesAreLoading,
}) => {
    const [field] = useField<IFormValues['developmentManager']>(DEVELOPMENT_MANAGER_FIELD_VALUE);
    const formikProps = useFormikContext<IFormValues>();
    const [selectedManagerType, setSelectedManagerType] = useState<string | undefined>(field?.value?.type);
    const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');

    const { isRequired, shouldRender } = useAttributeSuggested({
        attribute: DefaultAttributes.DEVELOPMENT_MANAGER,
        showSuggestedOnly,
    });

    // Initial type selection options
    const typeOptions: IOptionAnyValue[] = [
        {
            label: DEVELOPMENT_MANAGER_TYPE_TITLES.USERS.title,
            component: <DevelopmentManagerTypeOption
                title={DEVELOPMENT_MANAGER_TYPE_TITLES.USERS.title}
                subtitle={DEVELOPMENT_MANAGER_TYPE_TITLES.USERS.subtitle}
            />,
            value: DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.USER,
        },
        {
            label: DEVELOPMENT_MANAGER_TYPE_TITLES.CONTACTS.title,
            component: <DevelopmentManagerTypeOption
                title={DEVELOPMENT_MANAGER_TYPE_TITLES.CONTACTS.title}
                subtitle={DEVELOPMENT_MANAGER_TYPE_TITLES.CONTACTS.subtitle}
            />,
            value: DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.CONTACT,
        },
        {
            label: DEVELOPMENT_MANAGER_TYPE_TITLES.COMPANIES.title,
            component: <DevelopmentManagerTypeOption
                title={DEVELOPMENT_MANAGER_TYPE_TITLES.COMPANIES.title}
                subtitle={DEVELOPMENT_MANAGER_TYPE_TITLES.COMPANIES.subtitle}
            />,
            value: DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.COMPANY,
        },
    ];

    const getAllSearchableOptions = (): IOptionAnyValue[] => [
        ...userOptions.map(option => ({ ...option, optionType: 'user' })),
        ...companyOptions.map(option => ({ ...option, optionType: 'company' })),
        ...contactOptions.map(option => ({ ...option, optionType: 'contact' })),
    ];

    const getCurrentOptions = (): IOptionAnyValue[] => {
        if (!selectedManagerType) {
            if (inputValue.trim()) {
                return getAllSearchableOptions().filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
            }
            return typeOptions;
        }

        let options: IOptionAnyValue[] = [];
        switch (selectedManagerType) {
            case DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.USER:
                options = userOptions;
                break;
            case DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.CONTACT:
                options = contactOptions;
                break;
            case DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.COMPANY:
                options = companyOptions;
                break;
            default:
                options = [];
                break;
        }

        // Filter options based on search input when type is selected
        if (inputValue.trim()) {
            options = options.filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
        }

        return [
            {
                label: 'Back',
                value: BACK_OPTION_VALUE,
                component: (
                    <span className="text-primary">
                        <NSIcon icon={IconArrowLeft} className="mr-2" />
                        Back
                    </span>
                ),
            },
            ...options,
        ];
    };

    const handleSelectChange = (option: IOptionAnyValue & { optionType?: string } | undefined) => {
        if (!option) {
            setSelectedManagerType(undefined);
            formikProps.setFieldValue(DEVELOPMENT_MANAGER_FIELD_VALUE, undefined);
            return;
        }

        if (option.value === BACK_OPTION_VALUE) {
            // Go back to type selection and keep menu open
            setSelectedManagerType(undefined);
            setInputValue('');
            formikProps.setFieldValue(DEVELOPMENT_MANAGER_FIELD_VALUE, undefined);
            return;
        }

        if (!selectedManagerType) {
            if (option.optionType) {
                const type
                    = DEVELOPMENT_MANAGER_ASSOCIATION_TYPE[option.optionType.toUpperCase() as keyof typeof DEVELOPMENT_MANAGER_ASSOCIATION_TYPE];
                if (!type) return;
                formikProps.setFieldValue(DEVELOPMENT_MANAGER_FIELD_VALUE, {
                    correlationId: option.value,
                    type,
                });
                setSelectedManagerType(type);
                // Close menu when direct selection is made
                setMenuIsOpen(false);
            } else if (typeOptions.some(typeOpt => typeOpt.value === option.value)) {
                const selectedType = option.value;
                setSelectedManagerType(selectedType);
                // Don't set form value until an actual item is selected
            }
        } else {
            formikProps.setFieldValue(DEVELOPMENT_MANAGER_FIELD_VALUE, {
                correlationId: option.value,
                type: selectedManagerType,
            });
            // Close menu when final selection is made
            setMenuIsOpen(false);
        }
    };

    const isLoading = usersAreLoading || contactsAreLoading || companiesAreLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const currentOptions = useMemo(() => getCurrentOptions(), [selectedManagerType, inputValue, userOptions, contactOptions, companyOptions]);

    const getCurrentValue = (): IOptionAnyValue | undefined => {
        // Only show a selected value if we have a complete selection
        if (field.value?.correlationId && field.value?.type) {
            if (!selectedManagerType) {
                const allOptions = getAllSearchableOptions();
                return allOptions.find(option => option.value === field.value?.correlationId);
            }
            const optionsWithoutBack = currentOptions.slice(1); // Remove back option
            return optionsWithoutBack.find(option => option.value === field.value?.correlationId);
        }
        return undefined;
    };

    const currentValue = getCurrentValue();

    // Ensure back option and type options never show as selected values
    const isTypeOption = currentValue && typeOptions.some(typeOpt => typeOpt.value === currentValue.value);
    const safeCurrentValue = (currentValue?.value === BACK_OPTION_VALUE || isTypeOption) ? undefined : currentValue;

    const getLoadingState = (): boolean => {
        if (!selectedManagerType) return usersAreLoading || contactsAreLoading || companiesAreLoading;

        switch (selectedManagerType) {
            case DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.USER:
                return usersAreLoading;
            case DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.CONTACT:
                return contactsAreLoading;
            case DEVELOPMENT_MANAGER_ASSOCIATION_TYPE.COMPANY:
                return companiesAreLoading;
            default:
                return false;
        }
    };

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);
    };

    const handleMenuOpen = () => {
        setMenuIsOpen(true);
    };

    const handleMenuClose = () => {
        setMenuIsOpen(false);
    };

    const handleFocus = () => {
        setMenuIsOpen(true);
    };

    const formatOptionLabel = (option: IOptionAnyValue) => {
        if (option.component) {
            return option.component;
        }
        return option.label;
    };

    const CustomPlaceholder = (props: PlaceholderProps<IOptionAnyValue>) => (
        <components.Placeholder {...props}>
            {PLACEHOLDER_TEXT}
        </components.Placeholder>
    );

    const CustomSingleValue = ({ data, children, ...rest }: SingleValueProps<IOptionAnyValue>) => {
        const isTypeOption = typeOptions.some(typeOpt => typeOpt.value === data.value);
        if (data.value === BACK_OPTION_VALUE || isTypeOption) {
            const placeholderProps = {
                ...rest,
                isFocused: false,
                children: PLACEHOLDER_TEXT,
            };
            return <components.Placeholder {...placeholderProps} />;
        }
        return <components.SingleValue {...rest} data={data}>{children}</components.SingleValue>;
    };

    const CustomClearIndicator = (props: ClearIndicatorProps<IOptionAnyValue>) => {
        const { selectProps } = props;
        const { value } = selectProps;
        const isTypeOption = value && 'value' in value && typeOptions.some(typeOpt => typeOpt.value === value.value);
        if ((value && 'value' in value && value.value === BACK_OPTION_VALUE) || isTypeOption) {
            return null;
        }
        return <components.ClearIndicator {...props} />;
    };

    const customComponents = {
        DropdownIndicator: MemoizedDropdownIndicator,
        SingleValue: CustomSingleValue,
        ClearIndicator: CustomClearIndicator,
        Placeholder: CustomPlaceholder,
    };

    return (
        <RenderIf isTrue={shouldRender}>
            <div className="w-50">
                <NSSelect
                    key="development-manager-select"
                    name={DEVELOPMENT_MANAGER_FIELD_VALUE}
                    label={(
                        <span>
                            Development manager
                            {isRequired && <span className="text-danger"> *</span>}
                            {isLoading && (
                                <NSIcon
                                    icon={IconLoader2}
                                    spin
                                    className="ml-2"
                                    size="sm"
                                />
                            )}
                        </span>
                    )}
                    options={currentOptions}
                    value={safeCurrentValue}
                    onChange={handleSelectChange}
                    placeholder={PLACEHOLDER_TEXT}
                    isLoading={getLoadingState()}
                    isClearable
                    isSearchable
                    formikProps={formikProps}
                    components={customComponents}
                    formatOptionLabel={formatOptionLabel}
                    menuIsOpen={menuIsOpen}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                    onInputChange={handleInputChange}
                    onFocus={handleFocus}
                    closeMenuOnSelect={false}
                />
            </div>
        </RenderIf>
    );
};

export default DevelopmentManagerInput;
